import { useInfiniteQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldErrorMessage } from '@features/campaigns/builder/components/FormFields/common/FieldErrorMessage/FieldErrorMessage';
import { EnhancedDropdown } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { BillingFormFieldTypes } from '@features/campaigns/details/components/FormFields/FieldTypes.ts';
import { QUERY_KEYS } from '@features/campaigns/details/consts/queryKeys.ts';
import { fetchOneFlightClients } from '@features/campaigns/details/services/formFields.ts';
import { DropdownWithSearch } from '@features/campaigns/shared/components/FormFields/Dropdown/components/DropdownWithSearch/DropdownWithSearch.tsx';
import { getNextParamFn } from '@features/campaigns/shared/components/FormFields/Dropdown/utils/getNextParamFn';
import fieldStyles from '@shared/components/FormFields/FormFields.module.scss';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { useDebounce } from '@shared/hooks/useDebounce';

interface FieldClientProps {
  clearSelectedValuesHandler: () => void;
  restoredInputValue?: string;
  disabled: boolean;
  createdBy?: string;
  showCode: boolean;
}

export function FieldClient({
  clearSelectedValuesHandler,
  restoredInputValue,
  disabled,
  showCode = true,
}: FieldClientProps) {
  const componentRef = useRef<EnhancedDropdown>(null);
  const { control } = useFormContext();

  const [inputValue, setInputValue] = useState<string | undefined>(undefined);
  const [searchQueryValue, setSearchQueryValue] = useState<string | undefined>(undefined);
  const [previousRestoredInputValue, setPreviousRestoredInputValue] = useState(restoredInputValue);
  const debouncedQueryValue = useDebounce(searchQueryValue);
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.ONEFLIGHT_CLIENTS, debouncedQueryValue],
    queryFn: (queryParams) => {
      const search = debouncedQueryValue;
      const page = queryParams?.pageParam?.page || 1;

      return fetchOneFlightClients({ pageParam: { page, search } });
    },
    initialPageParam: { page: 1, search: debouncedQueryValue },
    getNextPageParam: getNextParamFn(debouncedQueryValue),
    refetchOnMount: false,
    gcTime: 0,
  });

  if (previousRestoredInputValue !== restoredInputValue) {
    setPreviousRestoredInputValue(restoredInputValue);
    if (!restoredInputValue) {
      setInputValue(undefined);
    }
  }

  return (
    <div className={fieldStyles.fieldWrapper}>
      <label htmlFor={BillingFormFieldTypes.CLIENT} className={fieldStyles.fieldLabel}>
        {t('builder.formFields.client.label')}
      </label>
      <Controller
        control={control}
        name={BillingFormFieldTypes.CLIENT}
        render={({ field, formState: { errors } }) => {
          return (
            <>
              <DropdownWithSearch
                fieldName={BillingFormFieldTypes.CLIENT}
                componentRef={componentRef}
                placeholder={t('builder.formFields.client.placeholder')}
                data={data}
                disabled={disabled}
                isFetching={isFetching}
                inputValue={inputValue}
                setInputValue={setInputValue}
                restoredInputValue={restoredInputValue}
                setSearchQueryValue={setSearchQueryValue}
                clearSelectedValuesHandler={clearSelectedValuesHandler}
                fieldController={field}
                fetchNextPage={fetchNextPage}
                showCode={showCode}
              />
              {errors?.[BillingFormFieldTypes.CLIENT] && (
                <FieldErrorMessage fieldErrorData={errors} fieldName={BillingFormFieldTypes.CLIENT} />
              )}
            </>
          );
        }}
      />
    </div>
  );
}
